import { useState, useEffect } from 'react';
import { AlertTriangle, CheckCircle, XCircle, Eye, User, MessageSquare } from 'lucide-react';
import { Loader } from './ui/Loader';
import { API_BASE } from '../utils/supabase/info';
import { formatDistanceToNow } from '../utils/date-utils';
import { toast } from '../utils/toast';
import { ConfirmDialog } from './ConfirmDialog';
import { useAdminModalZIndex } from './AdminModalZContext';
import { devError } from '../utils/logger';
import { fetchWithAuth } from '../utils/api';


interface AdminReportsProps {
  accessToken: string;
  subAdminId?: string; // ✅ NEW: Optional sub-admin session ID
}

export function AdminReports({ accessToken, subAdminId }: AdminReportsProps) {
  const adminModalZ = useAdminModalZIndex();
  const [reports, setReports] = useState<any>(null);
  const [reportFilter, setReportFilter] = useState<'all' | 'pending' | 'resolved'>('pending');
  const [loading, setLoading] = useState(true);
  
  // Confirm dialog states
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [showDismissConfirm, setShowDismissConfirm] = useState(false);
  const [reportToResolve, setReportToResolve] = useState<{id: string, type: string} | null>(null);
  const [resolvingApprove, setResolvingApprove] = useState(false);
  const [resolvingDismiss, setResolvingDismiss] = useState(false);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    setLoading(true);
    try {
      // ✅ Build headers with sub-admin session if present
      const headers: HeadersInit = {
        ...(subAdminId ? { 'X-SubAdmin-Session': subAdminId } : {}),
      };

      const response = await fetchWithAuth(
        `${API_BASE}/admin/reports`,
        { headers }
      );

      if (response.ok) {
        const data = await response.json();
        setReports(data);
      }
    } catch (error) {
      devError('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const resolveReport = async (reportId: string, reportType: 'profile' | 'message', action: 'approved' | 'dismissed') => {
    try {
      // ✅ Build headers with sub-admin session if present
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        ...(subAdminId ? { 'X-SubAdmin-Session': subAdminId } : {}),
      };

      const response = await fetchWithAuth(
        `${API_BASE}/admin/reports/resolve`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ report_id: reportId }),
        }
      );

      if (response.ok) {
        toast.success(`Rapport ${action === 'approved' ? 'godkjent' : 'avvist'}!`);
        fetchReports(); // Refresh to show updated status
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        devError('Backend error:', errorData);
        toast.error(errorData.error || 'Kunne ikke løse rapporten');
      }
    } catch (error) {
      devError('Error resolving report:', error);
      toast.error('Feil ved løsning av rapport');
    }
  };

  const handleApproveConfirm = async () => {
    if (!reportToResolve) return;
    setResolvingApprove(true);
    try {
      await resolveReport(reportToResolve.id, reportToResolve.type as 'profile' | 'message', 'approved');
      setShowApproveConfirm(false);
      setReportToResolve(null);
    } finally {
      setResolvingApprove(false);
    }
  };

  const handleDismissConfirm = async () => {
    if (!reportToResolve) return;
    setResolvingDismiss(true);
    try {
      await resolveReport(reportToResolve.id, reportToResolve.type as 'profile' | 'message', 'dismissed');
      setShowDismissConfirm(false);
      setReportToResolve(null);
    } finally {
      setResolvingDismiss(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <Loader size="lg" className="text-purple-600" />
        <p className="mt-4 text-gray-700">Laster rapporter...</p>
      </div>
    );
  }

  if (!reports) {
    return (
      <div className="text-center py-12 text-gray-500">
        Kunne ikke laste rapporter
      </div>
    );
  }

  // FIXED: Backend returns flat reports array, not separated profileReports/messageReports
  const allReports = reports.reports?.map((r: any) => ({
    reportId: r.id,
    type: r.reportType,
    reason: r.reason,
    description: r.description,
    status: r.status,
    createdAt: r.createdAt,
    reporterName: r.reporter?.name || 'Ukjent',
    reportedName: r.reported?.name || 'Ukjent',
    reportedEmail: r.reported?.email || '',
    reportedUserId: r.reportedUserId,
    messageId: r.messageId,
    messageContent: r.messageContent || '',
    messageSender: r.messageSender?.name || 'Ukjent', // ✅ NEW: Message sender name
    messageSenderEmail: r.messageSender?.email || '' // ✅ NEW: Message sender email
  })) || [];

  const filteredReports = allReports.filter((report) => {
    if (reportFilter === 'all') return true;
    if (reportFilter === 'pending') return report.status === 'pending';
    if (reportFilter === 'resolved') return report.status === 'resolved';
    return false;
  });

  const pendingCount = allReports.filter(r => r.status === 'pending').length;

  return (
    <div className="space-y-6">
      {/* Report Filter */}
      <div className="bg-white rounded-2xl p-4 shadow-lg border border-purple-100">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <h3 className="text-lg text-gray-900">Brukerrapporteringer</h3>
            {pendingCount > 0 && (
              <span className="px-3 py-1 bg-red-500 text-white rounded-full text-sm font-medium">
                {pendingCount} ventende
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setReportFilter('all')}
              className={`px-4 py-2 rounded-full transition-all ${
                reportFilter === 'all'
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Alle ({allReports.length})
            </button>
            <button
              onClick={() => setReportFilter('pending')}
              className={`px-4 py-2 rounded-full transition-all ${
                reportFilter === 'pending'
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Ventende ({pendingCount})
            </button>
            <button
              onClick={() => setReportFilter('resolved')}
              className={`px-4 py-2 rounded-full transition-all ${
                reportFilter === 'resolved'
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Løste ({allReports.filter(r => r.status === 'resolved').length})
            </button>
          </div>
        </div>
      </div>

      {/* Reports List */}
      {filteredReports.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center shadow-lg border border-purple-100">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-xl text-gray-900 mb-2">Ingen rapporter</h3>
          <p className="text-gray-600">
            {reportFilter === 'pending' 
              ? 'Alle rapporter er behandlet!' 
              : reportFilter === 'resolved'
              ? 'Ingen løste rapporter ennå'
              : 'Ingen rapporter funnet'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredReports.map((report) => (
            <div
              key={report.reportId}
              className={`bg-white rounded-2xl p-6 shadow-lg border-2 ${
                report.status === 'pending' 
                  ? 'border-red-200 bg-red-50' 
                  : 'border-green-200 bg-green-50'
              }`}
            >
              <div className="flex flex-col xl:flex-row items-start gap-4">
                {/* Left - Report Info */}
                <div className="flex-1 w-full">
                  <div className="flex items-center gap-3 mb-3 flex-wrap">
                    {report.type === 'profile' ? (
                      <div className="p-2 bg-purple-100 rounded-full">
                        <User className="w-5 h-5 text-purple-600" />
                      </div>
                    ) : (
                      <div className="p-2 bg-blue-100 rounded-full">
                        <MessageSquare className="w-5 h-5 text-blue-600" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg text-gray-900 font-medium">
                        {report.type === 'profile' ? 'Profilrapport' : 'Meldingsrapport'}
                      </h3>
                      <p className="text-sm text-gray-600">
                        Rapportert {formatDistanceToNow(report.createdAt)}
                      </p>
                    </div>
                    {report.status === 'pending' ? (
                      <span className="px-3 py-1 bg-red-500 text-white rounded-full text-sm flex items-center gap-1 whitespace-nowrap">
                        <AlertTriangle className="w-4 h-4" />
                        Ventende
                      </span>
                    ) : (
                      <span className="px-3 py-1 bg-green-500 text-white rounded-full text-sm flex items-center gap-1 whitespace-nowrap">
                        <CheckCircle className="w-4 h-4" />
                        Løst
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-4">
                    <div className="bg-white rounded-xl p-4 border border-gray-200">
                      <p className="text-sm text-gray-600 mb-1">Rapportert av</p>
                      <p className="text-gray-900 font-medium">{report.reporterName}</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 border border-gray-200">
                      <p className="text-sm text-gray-600 mb-1">
                        {report.type === 'profile' ? 'Rapportert bruker' : 'Avsender'}
                      </p>
                      <p className="text-gray-900 font-medium">
                        {report.type === 'profile' 
                          ? `${report.reportedName} (${report.reportedEmail})` 
                          : `${report.messageSender} (${report.messageSenderEmail})`}
                      </p>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-4 border border-gray-200 mb-4">
                    <p className="text-sm text-gray-600 mb-2">Årsak</p>
                    <p className="text-gray-900 font-medium mb-2">{report.reason}</p>
                    {report.description && (
                      <>
                        <p className="text-sm text-gray-600 mb-1">Beskrivelse</p>
                        <p className="text-gray-700">{report.description}</p>
                      </>
                    )}
                  </div>

                  {report.type === 'message' && report.messageContent && (
                    <div className="bg-gray-100 rounded-xl p-4 border border-gray-200">
                      <p className="text-sm text-gray-600 mb-2">Rapportert melding</p>
                      <p className="text-gray-900 italic">"{report.messageContent}"</p>
                    </div>
                  )}
                </div>

                {/* Right - Actions */}
                {report.status === 'pending' && (
                  <div className="flex flex-col gap-2 w-full xl:w-auto">
                    <button
                      onClick={() => {
                        setReportToResolve({id: report.reportId, type: report.type});
                        setShowApproveConfirm(true);
                      }}
                      className="px-4 py-2 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-all flex items-center justify-center gap-2 whitespace-nowrap w-full xl:w-auto"
                      title="Godkjenn rapport"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Godkjenn
                    </button>
                    <button
                      onClick={() => {
                        setReportToResolve({id: report.reportId, type: report.type});
                        setShowDismissConfirm(true);
                      }}
                      className="px-4 py-2 bg-gray-500 text-white rounded-xl hover:bg-gray-600 transition-all flex items-center justify-center gap-2 whitespace-nowrap w-full xl:w-auto"
                      title="Avvis rapport"
                    >
                      <XCircle className="w-4 h-4" />
                      Avvis
                    </button>
                    {report.type === 'profile' && (
                      <button
                        onClick={() => window.location.hash = `profile-${report.reportedUserId}`}
                        className="px-4 py-2 bg-purple-500 text-white rounded-xl hover:bg-purple-600 transition-all flex items-center justify-center gap-2 whitespace-nowrap w-full xl:w-auto"
                        title="Se profil"
                      >
                        <Eye className="w-4 h-4" />
                        Se profil
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Confirm Dialogs */}
      <ConfirmDialog
        isOpen={showApproveConfirm}
        onClose={() => {
          setShowApproveConfirm(false);
          setReportToResolve(null);
        }}
        onConfirm={handleApproveConfirm}
        title="Godkjenn rapport?"
        message="Er du sikker på at du vil godkjenne denne rapporten? Dette kan føre til tiltak mot brukeren."
        confirmText="Godkjenn"
        cancelText="Avbryt"
        variant="danger"
        loading={resolvingApprove}
        loadingText="Godkjenner..."
        portalZIndex={adminModalZ}
      />
      <ConfirmDialog
        isOpen={showDismissConfirm}
        onClose={() => {
          setShowDismissConfirm(false);
          setReportToResolve(null);
        }}
        onConfirm={handleDismissConfirm}
        title="Avvis rapport?"
        message="Er du sikker på at du vil avvise denne rapporten?"
        confirmText="Avvis"
        cancelText="Avbryt"
        variant="warning"
        loading={resolvingDismiss}
        loadingText="Avviser..."
        portalZIndex={adminModalZ}
      />
    </div>
  );
}
